import clsx from 'clsx';
import React from 'react';
import Icon from '../../atoms/Icon/Icon';
import usePagination from './usePagination';

export type PaginationProps = {
  ariaLabel?: string;
  currentPage: number;
  onPageChange: (page: number) => void;
  testId?: string;
  totalPages: number;
};

const Pagination = React.forwardRef<HTMLElement, PaginationProps>(
  ({ ariaLabel, currentPage, onPageChange, testId, totalPages }, ref) => {
    const {
      ariaNextLabel,
      ariaPrevLabel,
      canNextPage,
      canPreviousPage,
      items,
      onNextPage,
      onPreviousPage,
      resolvedAriaLabel,
    } = usePagination({ ariaLabel, currentPage, onPageChange, totalPages });

    return (
      <nav ref={ref} aria-label={resolvedAriaLabel} className="ui-pagination" data-testid={testId}>
        <ul className="ui-pagination__list">
          <li className="ui-pagination__item ui-pagination__item--nav">
            <button
              aria-disabled={!canPreviousPage}
              aria-label={ariaPrevLabel}
              className="ui-pagination__nav-btn"
              disabled={!canPreviousPage}
              onClick={onPreviousPage}
              type="button"
            >
              <Icon color="inherit" name="chevron-left" />
            </button>
          </li>

          {items.map((item) => {
            if (item.type === 'ellipsis') {
              return (
                <li key={item.key} aria-hidden="true" className="ui-pagination__item ui-pagination__item--ellipsis">
                  <span>…</span>
                </li>
              );
            }

            return (
              <li
                key={item.key}
                className={clsx('ui-pagination__item', { 'ui-pagination__item--active': item.isActive })}
              >
                <button
                  aria-current={item.isActive ? 'page' : undefined}
                  aria-label={item.ariaLabel}
                  className="ui-pagination__page-btn"
                  onClick={item.onClick}
                  type="button"
                >
                  {item.page}
                </button>
              </li>
            );
          })}

          <li className="ui-pagination__item ui-pagination__item--nav">
            <button
              aria-disabled={!canNextPage}
              aria-label={ariaNextLabel}
              className="ui-pagination__nav-btn"
              disabled={!canNextPage}
              onClick={onNextPage}
              type="button"
            >
              <Icon color="inherit" name="chevron-right" />
            </button>
          </li>
        </ul>
      </nav>
    );
  },
);

Pagination.displayName = 'Pagination';

export default Pagination;
